<template>
  <div>
    <h6>{{ question.label }} {{ question.required ? '*' : '' }}</h6>
    <div class="mb-2" v-html="question.description"></div>

    <!-- ───── Mode selector ───── -->

    <div class="sig-toggle blade-tabs mb-3 no-print" :style="{ borderBottomColor: buttonColor }">
      <span
        :class="['tab-option', mode === 'draw' ? 'active' : '']"
        :style="mode === 'draw' ? { color: buttonColor } : {}"
        @click="switchMode('draw')"
      >
        {{ trans('draw') }}
      </span>
      <span
        :class="['tab-option', mode === 'type' ? 'active' : '']"
        :style="mode === 'type' ? { color: buttonColor } : {}"
        @click="switchMode('type')"
      >
        {{ trans('type') }}
      </span>
    </div>

    <!-- ───── DRAW MODE ───── -->
    <div v-show="mode === 'draw'" class="signature wrapper">
      <signature-pad
        ref="signaturePad"
        :options="{ onBegin, onEnd }"
        class="signature-pad"
      />
      <a href="#" class="clear no-print" @click.prevent="onClear">×</a>
    </div>

    <!-- ───── TYPE MODE ───── -->
    <div v-show="mode === 'type'" class="typed-signature-container">
      <b-form-input
        v-model="typedName"
        :disabled="disabled"
        :placeholder="typedPlaceholder"
        class="mb-2 typed-signature-input"
      />

      <div class="custom-font-dropdown mb-2" @click="dropdownOpen = !dropdownOpen">
        <div class="selected" :style="{ fontFamily: selectedFont }">
          {{ currentFontLabel }}
          <span class="dropdown-arrow">▾</span>
        </div>
        <div class="dropdown-options" v-if="dropdownOpen">
          <div
            class="font-option"
            v-for="(font, i) in fonts"
            :key="font"
            @click.stop="selectFont(font)"
            :style="{ fontFamily: font }"
          >
            Style {{ i + 1 }}
          </div>
        </div>
      </div>

      <div
        class="typed-preview"
        :style="previewStyle"
        v-if="typedName"
      >
        {{ typedName }}
      </div>

      <a
        href="#"
        v-if="typedName"
        class="position-fixing clear no-print"
        @click.prevent="onClear"
      >×</a>
    </div>

    <input
      type="text"
      :name="`${question.slug}[draw]`"
      :required="question.required"
      :value="drawData"
      :class="{ 'is-invalid': showError || (errors && errors.length > 0) }"
      style="opacity:0;display:block;height:1px;padding:0;margin:0;"
      autocomplete="off"
    />

    <b-form-invalid-feedback v-if="showError || (errors && errors.length > 0)" class="d-block" style="max-width:300px;">
      {{ (errors && errors.length > 0) ? errors.join(' ') : (trans('signature_required') || 'Signature is required.') }}
    </b-form-invalid-feedback>
  </div>
</template>

<style scoped>
.dropdown-arrow {
  float: right;
  font-size: 16px;
  color: #b1bace;
  margin-left: 10px;
}
.custom-font-dropdown {
  border: 1px solid #b1bace;
  border-radius: 4px;
  cursor: pointer;
  max-width: 300px;
  position: relative;
}

.custom-font-dropdown .selected {
  padding: 8px 12px;
  font-size: 18px;
}

.custom-font-dropdown .dropdown-options {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  border: 1px solid #b1bace;
  background: white;
  z-index: 10;
}

.font-option {
  padding: 8px 12px;
  cursor: pointer;
  font-size: 18px;
}

.font-option:hover {
  background: #f0f0f0;
}

.sig-toggle.blade-tabs {
  display: flex;
  margin-bottom: 1rem;
}

.tab-option {
  margin-right: 20px;
  padding-bottom: 6px;
  font-weight: 500;
  font-size: 16px;
  color: #333;
  cursor: pointer;
  position: relative;
  transition: color 0.2s;
}

.tab-option.active {
  color: inherit;
}

.tab-option.active::after {
  content: '';
  position: absolute;
  left: 0;
  bottom: -1px;
  height: 2px;
  width: 100%;
  background-color: currentColor;
}

.signature.wrapper {
  width: 100%;
  max-width: 600px;
  margin: 0 auto;
  background-color: #f8f9fa;
  border: 2px solid #b1bace;
  border-radius: 8px;
  overflow: hidden;
  position: relative;
  padding: 0;
  display: block;
}

.signature-pad {
  width: 100% !important;
  height: 200px !important;
  background-color: white;
  display: block;
}

.clear {
  position: absolute;
  top: 8px;
  right: 12px;
  font-size: 24px;
  font-weight: bold;
  color: #f57a00;
  z-index: 10;
}

.position-fixing{
  top: 2px!important;
  right: 10px!important;
}

.typed-signature-container {
  position: relative;
  max-width: 300px;
}

.typed-signature-input {
  border-radius: 4px;
  width: 100%;
  box-sizing: border-box;
}

.typed-preview {
  border: 1px solid #b1bace;
  background: #fff;
  border-radius: 4px;
  padding: 10px;
  font-size: 30px;
  text-align: center;
  min-height: 100px;
  user-select: none;
  align-content: center;
}
</style>

<script>
import SignaturePad from 'vue-signature-pad'

export default {
  props: {
    value: {
      type: [String, Object],
      default: null
    },
    question: Object,
    disabled: { type: Boolean, default: false },
    size: { type: String, default: 'md' },
    errors: Array,
    buttonColor: {
      type: String,
      default: '#007bff'
    }
  },
  components: { SignaturePad },
  data() {
    return {
      dropdownOpen: false,
      trans: trans,
      drawData: typeof this.value === 'string'
                ? this.value
                : (this.value && this.value.draw) || null,
      typedData: this.value && typeof this.value === 'object'
                 ? this.value.typed
                 : '',
      mode: 'draw', // 'draw' | 'type'
      typedName: '',
      showError: false,
      selectedFont: 'Pacifico',
      hasInteracted: false,
      fonts: [ 'Pacifico', 'Great Vibes', 'Dancing Script', 'Satisfy', 'Brush Script MT' ],
      fontOptions: [
        { text: 'Style 1', value: 'Pacifico' },
        { text: 'Style 2', value: 'Great Vibes' },
        { text: 'Style 3', value: 'Dancing Script' },
        { text: 'Style 4', value: 'Satisfy' },
        { text: 'Style 5', value: 'Brush Script MT' },
      ]
    }
  },

  computed: {
    currentFontLabel() {
      const index = this.fonts.indexOf(this.selectedFont)
      return index >= 0 ? `Style ${index + 1}` : this.selectedFont
    },
    previewStyle () { return { fontFamily: this.selectedFont } },
    typedPlaceholder() {
      return this.trans('type_your_name') || 'Type your name ...'
    },
    answer() {
      if (this.mode === 'draw') {
        return this.drawData || null
      } else {
        return this.typedData || null
      }
    },
  },

  watch: {
    value(val) {
      if (val && typeof val === 'object') {
        if (val.mode && val.mode !== this.mode) this.mode = val.mode;
        if (val.font && val.font !== this.selectedFont) this.selectedFont = val.font;

        if (val.typed && val.typed !== this.typedData) {
          this.typedData = val.typed;
          this.typedName = val.typed;
        }

        if (val.draw) {
          this.drawData = val.draw;
          if (!this.hasInteracted) this.hasInteracted = true;

          this.$nextTick(() => {
            if (
              this.mode === 'draw' &&
              this.$refs.signaturePad &&
              typeof this.$refs.signaturePad.fromDataURL === 'function'
            ) {
              try {
                this.$refs.signaturePad.fromDataURL(this.drawData);
              } catch (err) {
                console.warn('Failed to load signature image into canvas', err);
              }
            }
          });
        }
      } else {
        // Legacy string-only value
        if (val !== this.drawData) {
          this.mode = 'draw';
          this.drawData = val;
          this.typedData = '';
          this.typedName = '';
          this.selectedFont = this.fonts[0];
          if (val) this.hasInteracted = true;
        }
      }
    },
    mode(newVal) {
      this.$nextTick(() => {
        if (newVal === 'draw' && this.$refs.signaturePad) {
          this.$refs.signaturePad.resizeCanvas()
        }
      })
      this.emitValue()
    },
    drawData:   'emitValue',
    typedData:  'emitValue',
    selectedFont: 'emitValue',
    typedName:    'updateTypedSignature',
  },

  mounted() {
    document.addEventListener('click', this.handleOutsideClick)
    this.fonts.forEach(f => document.fonts.load(`48px ${f}`))
  },
  beforeDestroy() {
    document.removeEventListener('click', this.handleOutsideClick)
  },
  methods: {
    /* ───────── Draw mode helpers ───────── */
    onInvalid() {
      this.$emit('validation-error', this.question.slug)
    },

    switchMode(selectedMode) {
      if (this.mode === selectedMode) return
      this.mode = selectedMode
    },

    onInput() {
      this.$emit('clear-error', this.question.slug)
    },

    onClear() {
      this.$emit('clear-error', this.question.slug)
      if (this.mode === 'draw') {
        this.$refs.signaturePad.clearSignature()
        this.drawData = ''
      } else {
        this.typedName = ''
        this.typedData = ''
        this.drawData = ''
      }
      this.hasInteracted = false
      this.emitValue()
    },
    isValid() {
      if (!this.question.required) {
        this.showError = false
        return true
      }

      var valid = false

      if (this.mode === 'draw') {
        valid = this.hasInteracted && !!this.drawData && !this.$refs.signaturePad?.isEmpty()
      } else if (this.mode === 'type') {
        valid = this.hasInteracted && !!this.typedName && !!this.drawData
      }

      this.showError = !valid
      return valid
    },
    onBegin() {
      this.$refs.signaturePad.resizeCanvas()
      this.$emit('clear-error', this.question.slug)
    },
    onEnd() {
      const { isEmpty, data } = this.$refs.signaturePad.saveSignature()
      this.drawData = isEmpty ? null : data
      this.hasInteracted = true
      this.showError = false
      this.emitValue()
    },

    /* ───────── Type mode helpers ───────── */
    updateTypedSignature() {
      if (this.mode !== 'type' || !this.typedName) {
        this.typedData = ''
        this.drawData = ''
        this.emitValue()
        return
      }

      const canvas = document.createElement('canvas')
      canvas.width = 600
      canvas.height = 200
      const ctx = canvas.getContext('2d')

      ctx.fillStyle = '#fff'
      ctx.fillRect(0, 0, canvas.width, canvas.height)
      ctx.font = `48px ${this.selectedFont}`
      ctx.fillStyle = '#000'
      ctx.textAlign = 'center'
      ctx.textBaseline = 'middle'
      ctx.fillText(this.typedName, canvas.width / 2, canvas.height / 2)


      const png = canvas.toDataURL('image/png')
      this.typedData = this.typedName
      this.drawData = png
      this.showError = false
      this.hasInteracted = true
      this.emitValue()
    },
    emitValue() {
      this.$emit('input', {
        mode: this.mode,
        draw: this.drawData || '',
        typed: this.mode === 'type' ? this.typedData : '',
        font: this.mode === 'type' ? this.selectedFont : ''
      })
    },
    selectFont(font) {
      this.selectedFont = font
      this.dropdownOpen = false
    },
    handleOutsideClick(event) {
      if (!this.$el.querySelector('.custom-font-dropdown')?.contains(event.target)) {
        this.dropdownOpen = false
      }
    },
  },
}
</script>
